import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Hreflang } from "@/i18n/LanguageContext";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-white" data-testid="terms-page">
      <Helmet>
        <title>Conditions Générales d'Utilisation | DevisMaison.be</title>
        <meta name="description" content="Conditions générales d'utilisation de DevisMaison.be - Plateforme de mise en relation entre particuliers et entrepreneurs en Belgique et Luxembourg." />
        <link rel="canonical" href="https://devismaison.be/conditions-generales" />
      </Helmet>
      <Hreflang frPath="/conditions-generales" nlPath="/nl/algemene-voorwaarden" />
      <Navbar />

      <main className="pt-24 pb-16 lg:pt-32 lg:pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-8">
            Conditions Générales d'Utilisation
          </h1>
          
          <p className="text-slate-600 mb-8">
            Dernière mise à jour : {new Date().toLocaleDateString("fr-BE", { day: "numeric", month: "long", year: "numeric" })}
          </p>

          <div className="prose prose-slate max-w-none">
            {/* Article 1 */}
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                Article 1 - Éditeur du site
              </h2>
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200 mb-4">
                <p className="text-slate-700 font-medium">Propriétaire du site : Stéphane Muller (personne physique)</p>
                <p className="text-slate-600 mt-3 font-medium">Exploitant : Muller Construction SRL</p>
                <p className="text-slate-600">N° BCE / TVA : BE 0803.324.50</p>
                <p className="text-slate-600">Siège social : Bertrix, Belgique</p>
                <p className="text-slate-600">Téléphone : +32 61 46 83 32</p>
                <p className="text-slate-600">Email : contact@devismaison.be</p>
                <p className="text-slate-600">Site web : www.devismaison.be</p>
              </div>
              <p className="text-slate-600 leading-relaxed mt-4">
                Stéphane Muller, en tant que propriétaire du site, se réserve le droit de changer d'exploitant à tout moment, sans que cela n'affecte la validité des présentes conditions générales. En cas de changement d'exploitant, les utilisateurs en seront informés par une mise à jour des présentes CGU.
              </p>
            </section>

            {/* Article 2 */}
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                Article 2 - Objet
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Les présentes conditions générales d'utilisation (CGU) ont pour objet de définir les modalités et conditions d'utilisation de la plateforme DevisMaison.be (ci-après "la Plateforme"), propriété de Stéphane Muller et exploitée par Muller Construction SRL, ainsi que les droits et obligations des utilisateurs. La Plateforme permet la mise en relation entre des particuliers souhaitant réaliser des travaux de construction ou de rénovation et des entrepreneurs professionnels.
              </p>
            </section>

            {/* Article 2 */}
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                Article 3 - Définitions
              </h2>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li><strong>"Plateforme"</strong> : le site web DevisMaison.be et l'ensemble de ses fonctionnalités</li>
                <li><strong>"Utilisateur"</strong> : toute personne accédant à la Plateforme</li>
                <li><strong>"Particulier"</strong> : personne physique soumettant une demande de devis pour des travaux</li>
                <li><strong>"Entrepreneur"</strong> : professionnel du bâtiment inscrit sur la Plateforme pour recevoir des demandes de devis</li>
                <li><strong>"Lead"</strong> : demande de devis qualifiée transmise par un Particulier</li>
                <li><strong>"Services"</strong> : l'ensemble des services proposés par la Plateforme</li>
              </ul>
            </section>

            {/* Article 3 */}
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                Article 4 - Acceptation des conditions
              </h2>
              <p className="text-slate-600 leading-relaxed">
                L'utilisation de la Plateforme implique l'acceptation pleine et entière des présentes CGU. Ces conditions sont accessibles sur le site et prévalent sur toute autre version ou tout autre document contradictoire. L'Éditeur se réserve le droit de modifier ces conditions à tout moment. Les modifications entrent en vigueur dès leur publication sur la Plateforme.
              </p>
            </section>

            {/* Article 4 */}
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                Article 5 - Services proposés
              </h2>
              
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3 mt-6">
                5.1 Pour les Particuliers
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                La Plateforme permet aux Particuliers de :
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                <li>Soumettre gratuitement des demandes de devis pour des travaux</li>
                <li>Recevoir jusqu'à 5 devis de la part d'Entrepreneurs qualifiés</li>
                <li>Comparer les offres et choisir librement l'Entrepreneur de leur choix</li>
              </ul>

              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3 mt-6">
                5.2 Pour les Entrepreneurs
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                La Plateforme permet aux Entrepreneurs de :
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Créer un compte professionnel gratuitement</li>
                <li>Consulter les demandes de devis correspondant à leur domaine d'activité</li>
                <li>Acheter des leads pour accéder aux coordonnées complètes des Particuliers</li>
                <li>Contacter directement les Particuliers pour proposer leurs services</li>
              </ul>
            </section>

            {/* Article 5 */}
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                Article 6 - Inscription et compte Entrepreneur
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                L'inscription en tant qu'Entrepreneur nécessite :
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                <li>D'être une entreprise légalement constituée en Belgique ou au Luxembourg</li>
                <li>De disposer d'un numéro de TVA valide</li>
                <li>De fournir des informations exactes et à jour</li>
                <li>De respecter les obligations professionnelles (assurances, agréments, etc.)</li>
              </ul>
              <p className="text-slate-600 leading-relaxed">
                L'Entrepreneur est responsable de la confidentialité de ses identifiants de connexion et de toutes les activités effectuées depuis son compte.
              </p>
            </section>

            {/* Article 6 */}
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                Article 7 - Tarification et paiement
              </h2>
              
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3 mt-6">
                6.1 Gratuité pour les Particuliers
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                L'utilisation de la Plateforme est entièrement gratuite pour les Particuliers.
              </p>

              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3 mt-6">
                7.2 Tarification pour les Entrepreneurs
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                L'inscription et la consultation des leads sont gratuites. L'achat de leads pour accéder aux coordonnées des Particuliers est payant. Les tarifs sont indiqués sur chaque lead et varient en fonction de la catégorie de travaux.
              </p>

              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3 mt-6">
                7.3 Paiement
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Les paiements sont effectues par virement bancaire SEPA. Lors de l'achat d'un lead, un QR code (norme europeenne EPC) est genere pour pre-remplir le virement dans votre application bancaire (beneficiaire, IBAN, montant, communication). Les coordonnees du Particulier sont communiquees des que le paiement est recu et valide. Aucune donnee de carte bancaire n'est collectee. Les prix sont indiques en euros (EUR), TVA comprise pour les entreprises belges et luxembourgeoises.
              </p>
            </section>

            {/* Article 7 */}
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                Article 8 - Obligations des utilisateurs
              </h2>
              
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3 mt-6">
                7.1 Obligations des Particuliers
              </h3>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                <li>Fournir des informations exactes et sincères dans les demandes de devis</li>
                <li>Ne pas soumettre de demandes fictives ou abusives</li>
                <li>Répondre aux sollicitations des Entrepreneurs dans un délai raisonnable</li>
              </ul>

              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3 mt-6">
                7.2 Obligations des Entrepreneurs
              </h3>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Fournir des informations d'entreprise exactes et à jour</li>
                <li>Respecter la confidentialité des données des Particuliers</li>
                <li>Ne pas revendre ou partager les leads achetés</li>
                <li>Contacter les Particuliers de manière professionnelle et respectueuse</li>
                <li>Respecter les engagements pris dans les devis</li>
                <li>Disposer des assurances et agréments requis pour leur activité</li>
              </ul>
            </section>

            {/* Article 8 */}
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                Article 9 - Responsabilité
              </h2>
              
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3 mt-6">
                8.1 Rôle de la Plateforme
              </h3>
              <p className="text-slate-600 leading-relaxed mb-4">
                La Plateforme agit uniquement en tant qu'intermédiaire technique facilitant la mise en relation entre Particuliers et Entrepreneurs. Elle n'est pas partie aux contrats conclus entre eux et ne peut être tenue responsable :
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
                <li>De la qualité, du prix ou de l'exécution des travaux</li>
                <li>Du comportement des utilisateurs</li>
                <li>De l'exactitude des informations fournies par les utilisateurs</li>
                <li>Des litiges survenant entre Particuliers et Entrepreneurs</li>
              </ul>

              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-3 mt-6">
                8.2 Disponibilité
              </h3>
              <p className="text-slate-600 leading-relaxed">
                La Plateforme s'efforce d'assurer la disponibilité continue de ses services mais ne peut garantir une disponibilité permanente. Des interruptions pour maintenance ou en cas de force majeure sont possibles.
              </p>
            </section>

            {/* Article 9 */}
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                Article 10 - Politique de remboursement
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Les leads achetés ne sont pas remboursables sauf dans les cas suivants :
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Coordonnées du Particulier invalides ou incorrectes (vérifiées par notre service)</li>
                <li>Lead en doublon (même projet déjà acheté)</li>
                <li>Projet manifestement fictif ou frauduleux</li>
              </ul>
              <p className="text-slate-600 leading-relaxed mt-4">
                Toute demande de remboursement doit être soumise dans les 7 jours suivant l'achat à contact@devismaison.be avec les justificatifs appropriés.
              </p>
            </section>

            {/* Article 10 */}
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                Article 11 - Propriété intellectuelle
              </h2>
              <p className="text-slate-600 leading-relaxed">
                L'ensemble des éléments de la Plateforme (textes, images, logos, base de données, logiciels) sont protégés par les droits de propriété intellectuelle. Toute reproduction, représentation ou exploitation non autorisée est interdite et peut faire l'objet de poursuites.
              </p>
            </section>

            {/* Article 11 */}
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                Article 12 - Protection des données
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Le traitement des données personnelles est régi par notre <a href="/politique-de-confidentialite" className="text-orange-500 hover:underline">Politique de Confidentialité</a>, conforme au RGPD et à la loi belge du 30 juillet 2018.
              </p>
            </section>

            {/* Article 12 */}
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                Article 13 - Résiliation
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                L'Entrepreneur peut clôturer son compte à tout moment en contactant notre service client. La Plateforme se réserve le droit de suspendre ou de résilier un compte en cas de :
              </p>
              <ul className="list-disc pl-6 text-slate-600 space-y-2">
                <li>Violation des présentes CGU</li>
                <li>Comportement frauduleux ou abusif</li>
                <li>Fourniture d'informations fausses</li>
                <li>Non-respect des obligations professionnelles</li>
              </ul>
            </section>

            {/* Article 13 */}
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                Article 13 - Droit applicable et juridiction
              </h2>
              <p className="text-slate-600 leading-relaxed">
                Les présentes CGU sont régies par le droit belge. En cas de litige, les parties s'efforceront de trouver une solution amiable. À défaut, les tribunaux de l'arrondissement judiciaire de Neufchâteau (Belgique) seront seuls compétents.
              </p>
            </section>

            {/* Article 14 */}
            <section className="mb-10">
              <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                Article 15 - Contact
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                Pour toute question concernant ces conditions générales :
              </p>
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <p className="text-slate-700 font-medium">Propriétaire du site : Stéphane Muller (personne physique)</p>
                <p className="text-slate-600 mt-3 font-medium">Exploitant : Muller Construction SRL</p>
                <p className="text-slate-600">N° BCE / TVA : BE 0803.324.50</p>
                <p className="text-slate-600">Email : <a href="mailto:contact@devismaison.be" className="text-orange-500 hover:underline">contact@devismaison.be</a></p>
                <p className="text-slate-600">Téléphone : +32 61 46 83 32</p>
                <p className="text-slate-600">Adresse : Bertrix, Belgique</p>
              </div>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
